import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Search,
  X,
  Save,
  ChevronLeft,
  Newspaper,
  Edit3,
  ImageIcon,
} from "lucide-react";
import { db, storage, auth } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const cx = (...list) => list.filter(Boolean).join(" ");

function AInput({ value, onChange, placeholder, disabled }) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        disabled:bg-slate-50 disabled:text-slate-400 placeholder:text-slate-400 transition"
    />
  );
}

function ATextarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent
        placeholder:text-slate-400 transition resize-none"
    />
  );
}

function Field({ label, children }) {
  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
        </label>
      )}
      {children}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
        {children}
      </span>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

function newsToForm(id, d) {
  return {
    _fbId: id,
    title: d.title || "",
    content: d.content || "",
    imageUrl: d.imageUrl || "",
    imageFile: null,
    imagePreview: d.imageUrl || "",
    extraImages: d.extraImages || ["", "", "", ""],
  };
}

export default function NewsEditTab({ loading, setLoading, showStatus }) {
  const [news, setNews] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const imageFileRef = useRef(null);
  const extraImageRefs = useRef([null, null, null, null]);
  const [extraImageFiles, setExtraImageFiles] = useState([
    null,
    null,
    null,
    null,
  ]);

  useEffect(() => {
    const q = query(collection(db, "haberler"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setNews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setDataLoading(false);
      },
      () => setDataLoading(false)
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return news;
    return news.filter((item) =>
      `${item.title || ""} ${item.content || ""}`
        .toLocaleLowerCase("tr")
        .includes(q)
    );
  }, [news, search]);

  const set = (patch) => setSelected((p) => ({ ...p, ...patch }));

  const handleSelect = (item) => {
    setSelected(newsToForm(item.id, item));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm(`"${item.title || "Bu haber"}" silinsin mi?`)) return;
    try {
      await deleteDoc(doc(db, "haberler", item.id));
      showStatus("success", "Haber silindi.");
    } catch (err) {
      showStatus("error", "Silinemedi: " + err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setLoading(true);
    try {
      const user = auth.currentUser;
      const { _fbId, imageFile, imagePreview, ...rest } = selected;

      let payload = { ...rest, updatedAt: serverTimestamp() };

      if (imageFile) {
        const fname = `news_${Date.now()}_${imageFile.name}`;
        const sRef = ref(storage, `haberler/${user?.uid}/${fname}`);
        await uploadBytes(sRef, imageFile);
        payload.imageUrl = await getDownloadURL(sRef);
      }

      const uploadedExtraImages = await Promise.all(
        (selected.extraImages || []).map(async (url, i) => {
          const file = extraImageFiles[i];
          if (file) {
            const fname = `news_extra_${i}_${Date.now()}_${file.name}`;
            const sRef = ref(storage, `haberler/${user.uid}/${fname}`);
            await uploadBytes(sRef, file);
            return await getDownloadURL(sRef);
          }
          return url || "";
        })
      );
      payload.extraImages = uploadedExtraImages;

      await updateDoc(doc(db, "haberler", _fbId), payload);
      showStatus("success", "Haber güncellendi.");
      setSelected(null);
      setExtraImageFiles([null, null, null, null]);
    } catch (err) {
      showStatus("error", "Hata: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Düzenleme formu ── */
  if (selected)
    return (
      <div>
        <button
          type="button"
          onClick={() => setSelected(null)}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5 transition"
        >
          <ChevronLeft className="w-4 h-4" /> Listeye Dön
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Edit3 className="w-4 h-4 text-amber-500" />
          <h3 className="font-semibold text-slate-900 text-sm">
            Haberi Düzenle
          </h3>
        </div>

        <form onSubmit={handleSave} className="space-y-5">
          <SectionHeading>Temel Bilgiler</SectionHeading>
          <div className="space-y-3">
            <Field label="Haber Başlığı">
              <AInput
                value={selected.title}
                onChange={(e) => set({ title: e.target.value })}
                placeholder="Başlık…"
              />
            </Field>
            <Field label="Haber İçeriği">
              <ATextarea
                value={selected.content}
                onChange={(e) => set({ content: e.target.value })}
                placeholder="Haber metni…"
                rows={8}
              />
            </Field>
          </div>

          <SectionHeading>Görsel</SectionHeading>

          {/* Önizleme */}
          {selected.imagePreview && (
            <div className="relative w-full max-h-52 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 group">
              <img
                src={selected.imagePreview}
                alt="önizleme"
                className="w-full h-full object-contain max-h-52"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <button
                type="button"
                onClick={() => {
                  set({ imageUrl: "", imagePreview: "", imageFile: null });
                  if (imageFileRef.current) imageFileRef.current.value = "";
                }}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-red-500 text-white shadow-md "
                title="Görseli kaldır"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <Field label="Görsel URL">
            <AInput
              value={selected.imageFile ? "" : selected.imageUrl}
              onChange={(e) => {
                set({
                  imageUrl: e.target.value,
                  imagePreview: e.target.value,
                  imageFile: null,
                });
                if (imageFileRef.current) imageFileRef.current.value = "";
              }}
              placeholder="https://…"
              disabled={!!selected.imageFile}
            />
          </Field>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-xs text-slate-400">veya dosya yükle</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm text-slate-700 transition">
              <ImageIcon className="w-4 h-4 text-slate-400" />
              {selected.imageFile ? selected.imageFile.name : "Dosya Seç"}
              <input
                ref={imageFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  set({
                    imageFile: f,
                    imagePreview: URL.createObjectURL(f),
                    imageUrl: "",
                  });
                }}
              />
            </label>
            {selected.imageFile && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    set({ imageFile: null, imagePreview: selected.imageUrl });
                    if (imageFileRef.current) imageFileRef.current.value = "";
                  }}
                  className="text-xs text-red-500 hover:text-red-700 transition"
                >
                  Kaldır
                </button>
                <img
                  src={URL.createObjectURL(selected.imageFile)}
                  alt="önizleme"
                  className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                />
              </>
            )}
          </div>

          <Field label="Ek Görseller (max 4)">
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => {
                const previewUrl = extraImageFiles[i]
                  ? URL.createObjectURL(extraImageFiles[i])
                  : selected.extraImages?.[i] || "";
                return (
                  <div key={i} className="flex items-center gap-2">
                    <AInput
                      value={
                        extraImageFiles[i]
                          ? ""
                          : selected.extraImages?.[i] || ""
                      }
                      onChange={(e) => {
                        const updated = [...(selected.extraImages || [])];
                        updated[i] = e.target.value;
                        set({ extraImages: updated });
                      }}
                      placeholder={`Görsel ${i + 1} URL… https://`}
                      disabled={!!extraImageFiles[i]}
                    />
                    <label className="shrink-0 flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-xs text-slate-600 transition">
                      <ImageIcon className="w-3.5 h-3.5 text-slate-400" />
                      {extraImageFiles[i]
                        ? extraImageFiles[i].name.slice(0, 10) + "…"
                        : "Dosya"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={(el) => (extraImageRefs.current[i] = el)}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const updated = [...extraImageFiles];
                          updated[i] = f;
                          setExtraImageFiles(updated);
                          const urls = [...(selected.extraImages || [])];
                          urls[i] = "";
                          set({ extraImages: urls });
                        }}
                      />
                    </label>
                    {previewUrl && (
                      <div className="relative shrink-0">
                        <img
                          src={previewUrl}
                          alt={`görsel ${i + 1}`}
                          className="w-10 h-10 rounded-lg object-cover border border-slate-200"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updatedFiles = [...extraImageFiles];
                            updatedFiles[i] = null;
                            setExtraImageFiles(updatedFiles);
                            const urls = [...(selected.extraImages || [])];
                            urls[i] = "";
                            set({ extraImages: urls });
                            if (extraImageRefs.current[i])
                              extraImageRefs.current[i].value = "";
                          }}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Field>

          <div className="pt-2 flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {loading ? "Kaydediliyor…" : "Güncelle"}
            </button>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="px-4 py-2.5 rounded-xl text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              İptal
            </button>
          </div>
        </form>
      </div>
    );

  /* ── Liste görünümü ── */
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Newspaper className="w-4 h-4 text-amber-500" />
        <h3 className="font-semibold text-slate-900 text-sm">Haberler</h3>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">
          {news.length}
        </span>
      </div>

      {/* Arama */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Başlık veya içerik ara…"
          className="w-full pl-9 pr-9 py-2.5 text-sm border border-slate-200 rounded-xl
            focus:outline-none focus:ring-2 focus:ring-slate-900 bg-slate-50 placeholder:text-slate-400"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <p className="text-xs text-slate-400 mb-3">
        <span className="font-medium text-slate-600">{filtered.length}</span>{" "}
        haber listeleniyor
      </p>

      {dataLoading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-14 bg-slate-100 rounded-xl animate-pulse"
            />
          ))}
        </div>
      )}

      {!dataLoading && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {filtered.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200
                hover:border-slate-300 hover:shadow-sm transition text-left group"
            >
              {/* Görsel */}
              <div className="w-10 h-10 rounded-lg border border-slate-100 bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center">
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <Newspaper className="w-4 h-4 text-slate-300" />
                )}
              </div>

              {/* Bilgi */}
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm text-slate-900 truncate block">
                  {item.title || "—"}
                </span>
                <span className="text-xs text-slate-400 truncate block">
                  {item.content ? item.content.slice(0, 60) + "…" : "—"}
                </span>
              </div>

              {/* Sil */}
              <button
                type="button"
                onClick={(e) => handleDelete(e, item)}
                className="shrink-0 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition"
                title="Sil"
              >
                <X className="w-4 h-4" />
              </button>

              <Edit3 className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 shrink-0 transition" />
            </button>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Newspaper className="w-8 h-8 mx-auto mb-2 text-slate-200" />
              <p className="text-sm">Haber bulunamadı.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
